import { useEffect, useState } from 'react';

import {
  type FocusSegment,
  type FocusSession,
  focusOverlaps,
  focusReview,
  focusTotals,
} from '../../shared/focus';
import type { LocalRecord, Task } from '../../shared/model';

import { workspace } from '../lib/engine';
import { Button, Disclosure, Input, Modal, Textarea } from './ui';

export const focusRuntime = crypto.randomUUID();

export function FocusActivity({ records, deviceId }: { records: LocalRecord[]; deviceId: string }) {
  const [reviewTaskId, setReviewTaskId] = useState<string>();

  useEffect(() => {
    // The heartbeat runs whether or not this tab is on screen. Gating it on visibility recorded
    // "last looked at" rather than "last alive", which stalled lastSeenAt for every session spent
    // working outside the tab and left the proposed end time short for abandoned ones.
    const timer = setInterval(() => {
      const rows = workspace.snapshot().records;

      for (const row of rows) {
        if (row.kind === 'focus') {
          const session = row.value as FocusSession;

          if (
            session.deviceId === deviceId &&
            session.endedAt === null &&
            session.segments.some(s => s.endedAt === null && s.runtimeId === focusRuntime)
          ) {
            // Automatic: if another device paused or ended this session in the
            // meantime, its version wins instead of asking the owner to choose.
            void workspace
              .save(
                row.id,
                'focus',
                {
                  ...session,
                  segments: session.segments.map(s =>
                    s.endedAt === null && s.runtimeId === focusRuntime
                      ? { ...s, lastSeenAt: Date.now() }
                      : s,
                  ),
                },
                { automatic: true },
              )
              .catch(() => {});
          }
        }
      }
    }, 30000);

    return () => clearInterval(timer);
  }, [deviceId]);

  const { overlaps, sessions: needsReview, taskIds } = focusReview(records);

  return (
    <>
      {taskIds.size > 0 && (
        <div className="global-notice notice focus-review-notice">
          Focus time needs review:{' '}
          {overlaps.length
            ? `${overlaps.length} overlapping segment ${overlaps.length === 1 ? 'pair' : 'pairs'}. `
            : ''}
          {needsReview.length
            ? `${needsReview.length} ${needsReview.length === 1 ? 'session contains' : 'sessions contain'} unconfirmed time. `
            : ''}
          Choose a task to confirm or correct its time. Unconfirmed time is excluded from confirmed
          totals.
          <div className="button-row">
            {[...taskIds].map(taskId => (
              <Button key={taskId} variant="secondary" onClick={() => setReviewTaskId(taskId)}>
                {focusTaskTitle(records, taskId)}
              </Button>
            ))}
          </div>
        </div>
      )}
      {reviewTaskId && (
        <FocusHistoryDialog
          records={records}
          taskId={reviewTaskId}
          close={() => setReviewTaskId(undefined)}
        />
      )}
    </>
  );
}

function focusTaskTitle(records: LocalRecord[], taskId: string) {
  const record = records.find(r => r.id === taskId && r.kind === 'task' && !r.deleted);

  return record ? (record.value as Task).title : 'Deleted task';
}

export function FocusHistoryDialog({
  records,
  taskId,
  close,
}: {
  records: LocalRecord[];
  taskId: string;
  close: () => void;
}) {
  return (
    <Modal
      open
      onClose={close}
      title={focusTaskTitle(records, taskId)}
      eyebrow="Focus time"
      closeLabel="Close time history"
    >
      <FocusHistory records={records} taskId={taskId} expanded />
    </Modal>
  );
}

export function FocusHistory({
  records,
  taskId,
  expanded = false,
}: {
  records: LocalRecord[];
  taskId: string;
  expanded?: boolean;
}) {
  const [editing, setEditing] = useState<{ record: LocalRecord; segment: FocusSegment }>();

  const sessions = records
    .filter(r => r.kind === 'focus' && (r.value as FocusSession).taskId === taskId)
    .sort((a, b) => (b.value as FocusSession).startedAt - (a.value as FocusSession).startedAt);

  const totals = focusTotals(records, taskId),
    overlaps = focusOverlaps(records);

  if (!sessions.length) {
    return null;
  }

  return (
    <Disclosure
      className="time-correction"
      open={expanded || undefined}
      summary={`Time history · ${Math.round(totals.confirmed)} confirmed min${
        totals.proposed ? ` · ${Math.round(totals.proposed)} proposed` : ''
      }`}
    >
      {sessions.map(r => (
        <div key={r.id}>
          {(r.value as FocusSession).segments.map(s => (
            <div className="history-row" key={s.id}>
              <span>
                {new Date(s.startedAt).toLocaleString()}
                <small>
                  {s.endedAt === null
                    ? 'Running · proposed'
                    : s.confirmed
                      ? 'Confirmed'
                      : 'Needs confirmation'}
                  {overlaps.some(pair => pair.some(x => x.segment.id === s.id))
                    ? ' · Overlaps another device'
                    : ''}
                </small>
                <small>{s.correctionNote}</small>
              </span>
              <Button variant="ghost" onClick={() => setEditing({ record: r, segment: s })}>
                Review time
              </Button>
            </div>
          ))}
        </div>
      ))}
      {editing && (
        <TimeCorrection
          key={editing.segment.id}
          record={editing.record}
          segment={editing.segment}
          records={records}
          close={() => setEditing(undefined)}
        />
      )}
    </Disclosure>
  );
}

function TimeCorrection({
  record,
  segment,
  close,
}: {
  record: LocalRecord;
  segment: FocusSegment;
  records: LocalRecord[];
  close: () => void;
}) {
  const input = (time: number) => new Date(time).toISOString().slice(0, 16);

  const [start, setStart] = useState(input(segment.startedAt)),
    [end, setEnd] = useState(input(segment.endedAt ?? segment.lastSeenAt)),
    [minutes, setMinutes] = useState(segment.correctedMinutes?.toString() ?? ''),
    [note, setNote] = useState(segment.correctionNote),
    [error, setError] = useState('');

  return (
    <Modal
      open
      onClose={close}
      title="Confirm or correct focus time"
      onSubmit={async e => {
        e.preventDefault();
        setError('');

        try {
          const current = workspace.snapshot().records.find(r => r.id === record.id)!;

          const session = current.value as FocusSession,
            taskRecord = workspace.snapshot().records.find(r => r.id === session.taskId)!;

          const startedAt = Date.parse(`${start}Z`),
            endedAt = Date.parse(`${end}Z`);

          if (!Number.isFinite(startedAt) || endedAt < startedAt || endedAt > Date.now()) {
            throw new Error('Choose a valid past interval.');
          }

          const corrected: FocusSegment = {
            ...(session.segments.find(s => s.id === segment.id) ?? segment),
            startedAt,
            endedAt,
            confirmed: true,
            correctedMinutes: minutes === '' ? null : Number(minutes),
            correctionNote: note.trim(),
          };

          const changes = [
            {
              ...current,
              value: {
                ...session,
                endedAt: segment.endedAt === null ? (session.endedAt ?? endedAt) : session.endedAt,
                segments: session.segments.map(s => (s.id === segment.id ? corrected : s)),
              },
            },
          ];

          if (segment.endedAt === null) {
            const task = taskRecord.value as Task;

            if (!task.done && !note.trim()) {
              throw new Error('Leave a stopping point when ending unfinished work.');
            }

            await workspace.saveMany([
              ...changes,
              {
                ...taskRecord,
                value: {
                  ...task,
                  stoppingPoint: task.done ? task.stoppingPoint : note.trim(),
                  focus: {
                    elapsedMs: task.focus?.elapsedMs ?? 0,
                    runningSince: null,
                    sessionOpen: false,
                    lastBreakMs: task.focus?.lastBreakMs ?? 0,
                  },
                },
              },
            ]);
          } else {
            await workspace.saveMany(changes);
          }

          close();
        } catch (e) {
          setError((e as Error).message);
        }
      }}
    >
      <p>
        Times below are UTC. Adjust the actual interval to resolve overlaps, or set credited minutes
        to zero for a duplicate.
      </p>
      <label>
        Start (UTC)
        <Input
          required
          type="datetime-local"
          value={start}
          onInput={e => setStart(e.currentTarget.value)}
        />
      </label>
      <label>
        End (UTC)
        <Input
          required
          type="datetime-local"
          value={end}
          onInput={e => setEnd(e.currentTarget.value)}
        />
      </label>
      <label>
        Credited minutes (blank uses interval)
        <Input
          type="number"
          min={0}
          max={10080}
          step="any"
          value={minutes}
          onChange={e => setMinutes(e.target.value)}
        />
      </label>
      <label>
        {segment.endedAt === null ? 'Where did you leave off?' : 'Correction note'}
        <Textarea value={note} onChange={e => setNote(e.target.value)} />
      </label>
      {error && <p className="error-message">{error}</p>}
      <div className="button-row">
        <Button variant="primary">Confirm time</Button>
        <Button variant="secondary" type="button" onClick={close}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
